'use client';

import React, { useState } from 'react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  Button,
  Accordion,
  AccordionItem,
} from '@heroui/react';
import Typography from '../typography/Typography';
import {
  OnlyFansIc,
  InstagramIc,
  TwitterIc,
  GoogleIc,
  IcReddit,
  IcTikTok,
  IcTg,
  IcYoutube,
  IcAnotherSoc,
  GlobalIc,
} from '../icons';

const SOURCES = [
  { key: 'only_fans', label: 'OnlyFans' },
  { key: 'reddit', label: 'Reddit' },
  { key: 'instagram', label: 'Instagram' },
  { key: 'tiktok', label: 'TikTok' },
  { key: 'google', label: 'Google' },
  { key: 'telegram', label: 'Telegram' },
  { key: 'twitter', label: 'Twitter' },
  { key: 'youtube', label: 'YouTube' },
  { key: 'another', label: 'Another' },
];

// Get source icon
const getSourceIcon = (source: string, isSelected: boolean, size: number = 16) => {
  const fill = isSelected ? '#11181C' : '#A8A8B0';
  const iconMap: Record<string, React.ReactElement> = {
    only_fans: <OnlyFansIc size={size} fill={fill} />,
    reddit: <IcReddit size={size} fill={fill} />,
    instagram: <InstagramIc size={size} fill={fill} />,
    tiktok: <IcTikTok size={size} fill={fill} />,
    google: <GoogleIc size={size} fill={fill} />,
    telegram: <IcTg size={size} fill={fill} />,
    twitter: <TwitterIc size={size} fill={fill} />,
    youtube: <IcYoutube size={size} fill={fill} />,
    another: <IcAnotherSoc size={size} fill={fill} />,
  };

  return iconMap[source?.toLowerCase()] || <GlobalIc size={size} />;
};

interface ResourcesFilterSelectProps {
  placeholder: string;
  allowedSource: string;
  excludedSource: string;
  onAllowedSourceChange: (source: string) => void;
  onExcludedSourceChange: (source: string) => void;
  startContent?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  variant?: 'default' | 'campaign';
  autoWidth?: boolean;
  t?: (key: string) => string;
}

const ResourcesFilterSelect: React.FC<ResourcesFilterSelectProps> = ({
  placeholder,
  allowedSource,
  excludedSource,
  onAllowedSourceChange,
  onExcludedSourceChange,
  startContent,
  className = '',
  disabled = false,
  variant = 'default',
  autoWidth = false,
  t = (key: string) => key,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const getVariantStyles = () => {
    switch (variant) {
      case 'campaign':
        return {
          trigger: 'bg-auth-surface border-none rounded-2 !px-3 !min-h-9 !h-9',
          value: 'text-sm !text-auth-muted',
        };
      default:
        return {
          trigger:
            'h-9 bg-gray-100 border-none rounded-2 !px-3 hover:bg-gray-200 focus:border-blue-500',
          value: 'text-sm !text-auth-muted',
        };
    }
  };

  const variantStyles = getVariantStyles();

  // Check if any filter is active
  const hasActiveFilters = allowedSource !== '' || excludedSource !== '';

  // Get display text
  const getDisplayText = () => {
    if (!hasActiveFilters) return placeholder;

    const count = (allowedSource ? 1 : 0) + (excludedSource ? 1 : 0);
    return `${placeholder} (${count})`;
  };

  const handleAllowedSelect = (sourceKey: string) => {
    // Toggle: if already selected, deselect; otherwise select
    if (allowedSource === sourceKey) {
      onAllowedSourceChange('');
    } else {
      onAllowedSourceChange(sourceKey);
    }
  };

  const handleExcludedSelect = (sourceKey: string) => {
    // Toggle: if already selected, deselect; otherwise select
    if (excludedSource === sourceKey) {
      onExcludedSourceChange('');
    } else {
      onExcludedSourceChange(sourceKey);
    }
  };

  return (
    <div className={autoWidth ? 'w-fit max-w-[20rem]' : 'w-full'}>
      <Popover
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        placement="bottom-start"
        classNames={{
          content: 'max-w-[21.25rem] w-[21.25rem] py-2 px-2',
        }}
      >
        <PopoverTrigger>
          <Button
            className={`${variantStyles.trigger} ${
              disabled ? 'opacity-50 cursor-not-allowed' : ''
            } ${autoWidth ? 'w-fit max-w-[20rem]' : 'w-full'} ${className} justify-start`}
            disabled={disabled}
          >
            <div className="flex items-center gap-1 w-full">
              {startContent}
              <span
                className={`${hasActiveFilters ? 'text-sm !text-auth-ink' : variantStyles.value} truncate flex-1 text-left`}
              >
                {getDisplayText()}
              </span>
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent>
          <div className="w-full">
            <Accordion
              selectionMode="multiple"
              className="px-0 gap-0"
              showDivider={false}
              itemClasses={{
                base: 'py-0 border-none',
                title: 'text-sm font-medium',
                trigger: 'py-0 h-8 px-2 data-[hover=true]:bg-transparent',
                content: 'pt-0 pb-2 px-2',
                indicator: 'text-auth-muted -rotate-90 data-[open=true]:rotate-0',
              }}
            >
              {/* Allowed sources */}
              <AccordionItem
                key="allowed"
                aria-label={t('partner.revshare.offers.filters.allowedSources') || 'Разрешенные'}
                title={
                  <div className="flex items-center gap-2">
                    <Typography variant="body2">
                      {t('partner.revshare.offers.filters.allowedSources') || 'Разрешенные'}
                    </Typography>
                    {allowedSource && <span className="text-xs text-auth-muted">(1)</span>}
                  </div>
                }
              >
                <div className="flex flex-col">
                  {SOURCES.map((source) => {
                    const isSelected = allowedSource === source.key;
                    return (
                      <div
                        key={`allowed-${source.key}`}
                        className={`flex items-center gap-2 py-2 px-2 rounded-2 cursor-pointer hover:bg-auth-surface transition-colors`}
                        onClick={() => handleAllowedSelect(source.key)}
                      >
                        {getSourceIcon(source.key, isSelected)}
                        <Typography
                          variant="body2"
                          className={isSelected ? '!text-auth-ink !font-medium' : ''}
                        >
                          {source.label}
                        </Typography>
                      </div>
                    );
                  })}
                </div>
              </AccordionItem>

              {/* Excluded sources */}
              <AccordionItem
                key="excluded"
                aria-label={
                  t('partner.revshare.offers.filters.excludedSources') || 'Не используются'
                }
                title={
                  <div className="flex items-center gap-2">
                    <Typography variant="body2">
                      {t('partner.revshare.offers.filters.excludedSources') || 'Не используются'}
                    </Typography>
                    {excludedSource && <span className="text-xs text-auth-muted">(1)</span>}
                  </div>
                }
              >
                <div className="flex flex-col">
                  {SOURCES.map((source) => {
                    const isSelected = excludedSource === source.key;
                    return (
                      <div
                        key={`excluded-${source.key}`}
                        className={`flex items-center gap-2 py-2 px-2 rounded-2 cursor-pointer hover:bg-auth-surface transition-colors`}
                        onClick={() => handleExcludedSelect(source.key)}
                      >
                        {getSourceIcon(source.key, isSelected)}
                        <Typography
                          variant="body2"
                          className={isSelected ? '!text-auth-ink !font-medium' : ''}
                        >
                          {source.label}
                        </Typography>
                      </div>
                    );
                  })}
                </div>
              </AccordionItem>
            </Accordion>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default ResourcesFilterSelect;
